import type {
  InnerCommentaryGroup,
  ProcessedCommentaryComment,
  ProcessedCommentaryImage,
} from "~/composables/processCommentarySearchResults/types";

/**
 * Helper interface for computed values in `CommentaryGroup*` components.
 *
 * Ideally we would like to use `(ProcessedCommentaryComment|ProcessedCommentaryImage)[]` here, but IntelliJ does not
 * deal well with union types in Pug templates.
 * See https://youtrack.jetbrains.com/issue/WEB-62974/Vue-type-narrowing-doesnt-work-in-Pug-template
 */
export interface DocComputed {
  type: "image" | "comment";

  /**
   * The node ID of the comment or image.
   */
  nid: number;

  /**
   * The comment, if type is "comment".
   */
  comment?: ProcessedCommentaryComment;

  /**
   * The image, if type is "image".
   */
  image?: ProcessedCommentaryImage;

  /**
   * Translations of the comment, if type is "comment".
   */
  otherComments?: ProcessedCommentaryComment[];

  /**
   * Translations of the image, if type is "image".
   */
  otherImages?: ProcessedCommentaryImage[];
}

export function computeDocList(
  docs: Array<ProcessedCommentaryComment | ProcessedCommentaryImage> = [],
): DocComputed[] {
  const uniqueNidSorted = [...new Set(docs.map((doc) => doc.nid))];
  const groupedByNid: {
    [nid: number]: ProcessedCommentaryComment[] | ProcessedCommentaryImage[];
  } = docs.reduce(
    (
      acc: {
        [nid: number]:
          | ProcessedCommentaryComment[]
          | ProcessedCommentaryImage[];
      },
      currentDocItem: any, // TypeCast - TypeScript don't know that for same nid, it's same type
    ) => {
      const nid = currentDocItem.nid;
      acc[nid] = [...(acc[nid] ?? []), currentDocItem];
      return acc;
    },
    {},
  );

  return uniqueNidSorted.map((nid): DocComputed => {
    const commentOrImage: any[] = groupedByNid[nid] ?? []; // TypeCast
    const [first, ...other] = commentOrImage;
    const type = first?.type;
    if (type === "comment") {
      return { type: "comment", comment: first, otherComments: other, nid };
    }
    return { type: "image", image: first, otherImages: other, nid };
  });
}

export type ComputedSubGroups = {
  subGroups: InnerCommentaryGroup[]; // List of "filtred" comments to display (Unique nid)
  otherCommentsByNid: OtherCommentsByNid; // By nid, translations available of comments in subGroups
};

export type OtherCommentsByNid = {
  [nid: number]: Array<ProcessedCommentaryComment | ProcessedCommentaryImage>;
};

/**
 * Clean lists of comment by removing other translations of a comment (having the same nid)
 * and store it on a map, keyed by nid, for later use in the `mergeMissingTranslationOnDocComputed` function.
 *
 * @param propsSubGroups
 *   Array of groups of comments in different languages.
 */
export function computeSubGroups(
  propsSubGroups: InnerCommentaryGroup[],
): ComputedSubGroups {
  const otherCommentsByNid: {
    [nid: number]: Array<ProcessedCommentaryComment | ProcessedCommentaryImage>;
  } = {};
  const subGroups: InnerCommentaryGroup[] = propsSubGroups
    .map((group: InnerCommentaryGroup) => {
      const newDocs = group.doclist.docs.filter((doc) => {
        const nid = doc.nid;
        if (otherCommentsByNid[nid]) {
          // If true (an array), we already see this comment nid on previous SubGroups doc
          otherCommentsByNid[nid].push(doc); // Store it on the global map
          return false; // don't keep it
        }
        otherCommentsByNid[doc.nid] = []; // First time we see this comment, add it on the map to let store translations when we see it
        return true;
      });
      group.doclist.docs = newDocs; // Replace group list by the filtred one
      group.doclist.numFound = newDocs.length; // Set the good count
      return group;
    })
    .filter((group) => {
      return group.doclist.numFound > 0;
    });

  return {
    subGroups,
    otherCommentsByNid,
  };
}
/**
 * Enhance comments (docs) by adding the translations of each comment or image if available.
 *
 * @param docs List of comments to display.
 * @param translations Map of translations of comments, keyed by nid.
 */
export function mergeMissingTranslationOnDocComputed(
  docs: DocComputed[],
  translations?: OtherCommentsByNid,
) {
  if (!translations) {
    return docs;
  }
  return docs.map((doc) => {
    const nid = doc.nid;
    const others: any = translations[nid];
    if (others) {
      // If it exists translations for a comment or image, add it
      if (doc.type === "comment") {
        doc.otherComments = [...(doc.otherComments ?? []), ...others];
        return doc;
      }
      doc.otherImages = [...(doc.otherImages ?? []), ...others];
      return doc;
    }
    return doc;
  });
}
